import { templateApiClient } from "@/templates/templateApiClient";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import {
  AccountCompanyDetails,
  EnterpriseAccountFeature,
  EnterpriseAccountFeatureType,
  failure,
  hasAccountFeature,
  isFailure,
  isOk,
  ok,
  pending,
  Result,
} from "@/common/common";
import { StringFieldModel } from "@/common/commonForms";
import {
  DCEvent,
  navigate,
  showGeneralError,
  showSuccess,
  updateStore,
} from "@/events/eventsDomain";
import { TabProps } from "@/components-v2/Tabs";

import { DropdownMenuItems, DropdownMenuProps } from "@/components-v2/Dropdown";
import { orderlessFilter } from "@/utils/filter";
import { CreateProjectRequest, projectApi } from "@/dbot/common/projectApi";

import {
  MapTemplateContent,
  MapTemplateDTO,
  MapTemplateGraphType,
  mapTemplateLabels,
  MapTemplateSetting,
  ProjectTemplateAttachmentDTO,
  ProjectTemplateContent,
  ProjectTemplateDataDTO,
  ProjectTemplateDocumentDTO,
  ProjectTemplateDTO,
  ProjectTemplateMatterDTO,
  ProjectTemplateRecipient,
  ProjectTemplateRecipientCategory,
  TemplateContent,
  TemplateDTO,
  TemplateType,
} from "@/templates/templateDomain";
import {
  checkedFiles,
  mapTemplates,
  MapTemplateState,
  ProjectTemplateDialogState,
  projectTemplates,
  ProjectTemplateState,
  ScopeRemovals,
  SelectedTemplateState,
  templateContentEvent,
  TemplatePreviewCompany,
  TemplateScope,
  TemplateSource,
  TemplateState,
  updateTemplateState,
  withTemplateState,
} from "@/templates/templateStateDomain";

import { adminProject } from "@/utils/navigation";
import { Language } from "@/i18n/languages";
import {
  DocumentType,
  getDocumentLabel,
  isBoardMeetingDocumentType,
  isDocumentType,
  isGeneralMeetingDocumentType,
  isMinuteDocumentType,
} from "@/common/documents";
import { getDecisionIcon, getDecisionName } from "@/dbot/common/commonDomain";
import { BgColor } from "@/components-v2/colors";
import { IconName } from "@/components-v2/iconMapping";
import { IconProps } from "@/components-v2/Icon";
import { AgendaItemCardProps } from "@/meetings/meetingsComponents";
import { InfoTagProps } from "@/components-v2/InfoTag";
import { compact, omit } from "lodash";
import { SelectProps, SelectWithNavigatorProps } from "@/components-v2/Select";
import { ownershipGraphProps } from "@/overview/ownership/commonOwnershipDomain";
import type { OwnershipEdge, OwnershipNode } from "@/overview/ownership/ownershipDomain";
import type { DataGraphProps } from "@/components-v2/DataGraph/dataGraphDomain";
import { when } from "@/utils/functions";
import { mapTemplateSettingLabels, templateDocumentTitle } from "@/templates/saveTemplateDomain";

type DetailsPanelTab = { id: string; type: "details"; label: TMessage };

export type ProjectTemplatePanelTab =
  | { id: string; type: "matters"; label: TMessage; items: TemplateMatterItem[] }
  | { id: string; type: "document"; label: TMessage; item: TemplateDocumentItem }
  | DetailsPanelTab;

export type MapTemplatePanelTab = { id: string; type: "map"; label: TMessage } | DetailsPanelTab;

export type TemplatePanelTab = ProjectTemplatePanelTab | MapTemplatePanelTab;

export const templatePanelTabs = (
  template: TemplateDTO,
  language: Language,
): TemplatePanelTab[] => {
  switch (template.data.type) {
    case TemplateType.PROJECT:
      return projectTemplatePanelTabs(template.data, language);
    case TemplateType.MAP:
      return mapTemplatePanelTabs;
  }
};

const detailsTab: DetailsPanelTab = {
  id: "details",
  type: "details",
  label: { key: "enterprise-account.templates.tab.details" },
};

const mapTemplatePanelTabs: MapTemplatePanelTab[] = [
  { id: "map", type: "map", label: { key: "preview" } },
  detailsTab,
];

const projectTemplatePanelTabs = (
  data: ProjectTemplateDataDTO,
  language: Language,
): ProjectTemplatePanelTab[] => {
  const matters = templateMatters(data, language);
  const mattersTab: ProjectTemplatePanelTab[] =
    matters.length === 0
      ? []
      : [
          {
            id: "matters",
            type: "matters",
            label: { key: "enterprise-account.templates.filter.matters" },
            items: matters,
          },
        ];
  const documentTabs: ProjectTemplatePanelTab[] = templateDocuments(data, language).map((item) => ({
    id: item.documentId,
    type: "document",
    label: item.label,
    item,
  }));
  return [...documentTabs, ...mattersTab, detailsTab];
};

export type TemplateDocumentItem = TemplateItem & { documentId: string; isPersonalised: boolean };

export type TemplateMatterItem = {
  decisionId: string;
  title: TMessage;
  isInformational: boolean;
  text?: string;
  attachments: ProjectTemplateAttachmentDTO[];
};

export const templateMatters = (
  data: ProjectTemplateDataDTO,
  language: Language,
): TemplateMatterItem[] =>
  data.matters.map((matter) => ({
    decisionId: matter.id,
    title: getDecisionName(matter.type),
    isInformational: matter.isInformational,
    text: matterText(matter, language),
    attachments: matter.attachments,
  }));

export const templateAttachmentUrl = (
  source: TemplateSource,
  templateId: string,
  attachmentId: string,
): string =>
  source.type === "account"
    ? `/api/enterprise-accounts/${source.accountId}/project-templates/${templateId}/attachments/${attachmentId}`
    : projectApi.projectTemplateAttachmentUrl({ orgNo: source.orgNo, templateId, attachmentId });

const accountTemplateDocumentPdfUrl = (
  accountId: string,
  templateId: string,
  documentId: string,
  recipientId?: string,
): string => {
  const url = `/api/enterprise-accounts/${accountId}/project-templates/${templateId}/documents/${documentId}/pdf`;
  return recipientId ? `${url}?${new URLSearchParams({ recipientId }).toString()}` : url;
};

export const templateDocumentPdfUrl = (
  source: TemplateSource,
  templateId: string,
  documentId: string,
  recipientId?: string,
  scopeRemovals?: ScopeRemovals,
): string =>
  source.type === "account"
    ? accountTemplateDocumentPdfUrl(source.accountId, templateId, documentId, recipientId)
    : projectApi.projectTemplateDocumentPdfUrl({
        orgNo: source.orgNo,
        templateId,
        documentId,
        recipientId,
        removedDecisionIds: scopeRemovals?.decisionIds,
        removedAttachmentIds: scopeRemovals?.attachmentIds,
      });

export type TemplateDocumentFile = { id: string; filename: string; url: string };

export const templateDocumentFiles = (
  source: TemplateSource,
  template: ProjectTemplateDTO,
  documentId: string,
): TemplateDocumentFile[] =>
  (template.data.documents.find((document) => document.id === documentId)?.attachments ?? []).map(
    (file) => ({
      id: file.id,
      filename: file.filename,
      url: templateAttachmentUrl(source, template.id, file.id),
    }),
  );

const templateRecipientCategories: ProjectTemplateRecipientCategory[] = [
  "SHAREHOLDER",
  "BOARD_MEMBER",
];

const templateRecipientCategoryLabels: { [key in ProjectTemplateRecipientCategory]: TMessageKey } =
  {
    SHAREHOLDER: "dbot.template-preview.shareholder",
    BOARD_MEMBER: "dbot.template-preview.board-member",
  };

export type RecipientPreview = {
  previewAs: SelectProps<ProjectTemplateRecipientCategory>;
  recipient?: SelectWithNavigatorProps<ProjectTemplateRecipient>;
  recipientId?: string;
};

type RecipientChoice = {
  previewAs?: ProjectTemplateRecipientCategory;
  selectedRecipientId?: string;
};

const availableCategories = (
  recipients: ProjectTemplateRecipient[],
): ProjectTemplateRecipientCategory[] =>
  templateRecipientCategories.filter((category) =>
    recipients.some((recipient) => recipient.category === category),
  );

const companyHasNoneLabels: { [key in ProjectTemplateRecipientCategory]: TMessageKey } = {
  SHAREHOLDER: "dbot.template-preview.no-shareholder",
  BOARD_MEMBER: "dbot.template-preview.no-board-member",
};

const chosenRecipients = (recipients: ProjectTemplateRecipient[], choice: RecipientChoice) => {
  const available = availableCategories(recipients);
  const previewAs = available.find((category) => category === choice.previewAs) ?? available[0];
  if (!previewAs) return undefined;
  const shown = recipients.filter((recipient) => recipient.category === previewAs);
  const index = Math.max(
    0,
    shown.findIndex((recipient) => recipient.id === choice.selectedRecipientId),
  );
  return { previewAs, shown, index };
};

export const recipientPreview = (
  recipients: ProjectTemplateRecipient[],
  choice: RecipientChoice,
  actions: {
    selectPreviewAs: (category: ProjectTemplateRecipientCategory) => void;
    selectRecipient: (recipient: ProjectTemplateRecipient) => void;
  },
): RecipientPreview | undefined => {
  const chosen = chosenRecipients(recipients, choice);
  if (!chosen) return undefined;
  const { previewAs, shown, index } = chosen;
  const available = availableCategories(recipients);
  const selected = shown[index];
  const previous = shown[index - 1];
  const next = shown[index + 1];
  return {
    recipientId: selected?.id,
    previewAs: {
      value: previewAs,
      items: templateRecipientCategories,
      idFn: (category) => category,
      labelFn: (category) => ({ key: templateRecipientCategoryLabels[category] }),
      disabledReasonFn: (category) =>
        available.includes(category) ? undefined : { key: companyHasNoneLabels[category] },
      onSelect: actions.selectPreviewAs,
    },
    recipient: when(selected, (value) => ({
      value,
      items: shown,
      idFn: (recipient) => recipient.id,
      labelFn: (recipient) => recipient.name,
      onSelect: actions.selectRecipient,
      selectPrev: when(previous, (recipient) => () => actions.selectRecipient(recipient)),
      selectNext: when(next, (recipient) => () => actions.selectRecipient(recipient)),
    })),
  };
};

export const templateMatterCard = (
  item: TemplateMatterItem,
  actions: {
    onOpen: (decisionId: string) => void;
    attachmentUrl: (attachmentId: string) => string;
  },
): AgendaItemCardProps => ({
  id: item.decisionId,
  title: item.title,
  subtitleIcon: item.isInformational ? "chats" : "gavel",
  subtitleText: item.isInformational
    ? { key: "dbot.information-matter" }
    : { key: "dbot.decision-matter" },
  infoTags: compact<InfoTagProps>([
    item.attachments.length > 0 && {
      id: "attachments",
      text: { translated: String(item.attachments.length) },
      icon: "paperclip",
      detailsHeading: { key: "attachments" },
      details: item.attachments.map((attachment) => ({
        id: attachment.id,
        icon: "paperclip",
        text: { translated: attachment.filename },
        onOpen: [
          {
            type: "preview-file",
            filename: attachment.filename,
            file: { url: actions.attachmentUrl(attachment.id) },
          },
        ],
      })),
    },
  ]),
  onOpen: () => actions.onOpen(item.decisionId),
});

const matterText = (matter: ProjectTemplateMatterDTO, language: Language): string | undefined =>
  (matter.texts.find((it) => it.language === language) ?? matter.texts[0])?.text;

const allDocuments = (data: ProjectTemplateDataDTO): ProjectTemplateDocumentDTO[] =>
  data.documents.filter((document) => isDocumentType(document.type));

export const templateDocuments = (
  data: ProjectTemplateDataDTO,
  language: Language,
): TemplateDocumentItem[] =>
  allDocuments(data).map(({ id, type, isPersonalised, title }) => ({
    documentId: id,
    label: templateDocumentTitle(title, language) ?? getDocumentLabel(type),
    icon: isMeetingDocumentType(type) ? "calendar" : "file-text",
    isPersonalised,
  }));

export const templatePreviewCompanies = (
  companies: AccountCompanyDetails[],
  features: EnterpriseAccountFeature[],
): TemplatePreviewCompany[] =>
  companies
    .filter((company) =>
      hasAccountFeature(features, EnterpriseAccountFeatureType.TEMPLATES, company.companyId),
    )
    .map((company) => ({
      companyId: company.companyId,
      orgNo: company.orgNo,
      name: company.companyName,
    }));

const noRemovals: ScopeRemovals = { decisionIds: [], attachmentIds: [] };

export type AccountAdminTemplateActions = {
  openSettings: (template: TemplateDTO) => DCEvent[];
  openDelete: (template: TemplateDTO) => DCEvent[];
};

export type CompanyAdminTemplateActions = {
  scope?: TemplateScope;
  isCreating: boolean;
  openScope: (template: TemplateDTO) => DCEvent[];
};

export type TemplateViewContext =
  | {
      type: "account";
      accountName: string;
      accountAdminActions: AccountAdminTemplateActions;
      companies: TemplatePreviewCompany[];
    }
  | { type: "company"; companyAdminActions: CompanyAdminTemplateActions };

const updateSelectedTemplateState = (
  changes: (selected: ProjectTemplateState, state: TemplateState) => Partial<ProjectTemplateState>,
): DCEvent =>
  updateTemplateState((state) => {
    const selected = state.selected;
    switch (selected?.type) {
      case TemplateType.PROJECT:
        return { selected: { ...selected, ...changes(selected, state) } };
      case TemplateType.MAP:
      case undefined:
        return {};
    }
  });

const withoutRemoved = (
  template: ProjectTemplateDTO,
  removed: ScopeRemovals,
): ProjectTemplateDTO => ({
  ...template,
  data: {
    ...template.data,
    matters: template.data.matters
      .filter((matter) => !removed.decisionIds.includes(matter.id))
      .map((matter) => ({
        ...matter,
        attachments: checkedFiles(matter.attachments, removed.attachmentIds),
      })),
    documents: template.data.documents.map((document) => ({
      ...document,
      attachments: checkedFiles(document.attachments, removed.attachmentIds),
    })),
  },
});

export const templateInScope = (selected: ProjectTemplateState): ProjectTemplateDTO =>
  withoutRemoved(selected.template, selected.scopeRemovals);

const shownTemplate = (selected: SelectedTemplateState): TemplateDTO => {
  switch (selected.type) {
    case TemplateType.PROJECT:
      return templateInScope(selected);
    case TemplateType.MAP:
      return selected.template;
  }
};

const panelTabs = (state: TemplateState, language: Language): TemplatePanelTab[] =>
  state.selected ? templatePanelTabs(shownTemplate(state.selected), language) : [];

const panelTabIds = (template: TemplateDTO): string[] => {
  switch (template.data.type) {
    case TemplateType.PROJECT:
      return [
        ...allDocuments(template.data).map((document) => document.id),
        ...(template.data.matters.length > 0 ? ["matters"] : []),
        "details",
      ];
    case TemplateType.MAP:
      return ["map", "details"];
  }
};

const selectedPanelTabId = (state: TemplateState, template: TemplateDTO): string | undefined => {
  const ids = panelTabIds(template);
  return ids.find((id) => id === state.panelTabId) ?? ids[0];
};

export const selectedPanelTab = (
  state: TemplateState,
  language: Language,
): TemplatePanelTab | undefined => {
  const tabId = state.selected && selectedPanelTabId(state, shownTemplate(state.selected));
  return panelTabs(state, language).find((tab) => tab.id === tabId);
};

export const prepTemplatePanelTabs = (state: TemplateState, language: Language): TabProps[] => {
  const selected = selectedPanelTab(state, language);
  return panelTabs(state, language).map((tab) => ({
    id: tab.id,
    label: tab.label,
    isActive: tab.id === selected?.id,
    onSelect: [updateTemplateState(() => ({ panelTabId: tab.id })), updateSelectedRecipient()],
  }));
};

const projectContent = (
  result?: Result<TemplateContent>,
): Result<ProjectTemplateContent> | undefined => {
  if (!isOk(result)) return result;
  switch (result.data.type) {
    case TemplateType.PROJECT:
      return ok(result.data);
    case TemplateType.MAP:
      return failure();
  }
};

const setTemplateContent = (templateId: string, result: Result<TemplateContent>): DCEvent[] => [
  updateTemplateState((state) => ({ contents: { ...state.contents, [templateId]: result } })),
  updateSelectedTemplateState((selected) =>
    selected.template.id === templateId ? { content: projectContent(result) } : {},
  ),
];

const fetchTemplateContent = (): DCEvent[] => [
  withTemplateState((state) => {
    const templateId = state.selected?.template.id;
    if (!templateId || state.contents[templateId]) return [];
    return [
      ...setTemplateContent(templateId, pending()),
      templateContentEvent(state.source, templateId, {
        onSuccess: (res) => [
          ...setTemplateContent(templateId, ok(res.data)),
          updateSelectedRecipient(),
        ],
        onFailure: () => setTemplateContent(templateId, failure()),
      }),
    ];
  }),
];

const selectedMatters = (state: TemplateState, language: Language): TemplateMatterItem[] => {
  const tab = selectedPanelTab(state, language);
  return tab?.type === "matters" ? tab.items : [];
};

export const templateMatterCards = (
  state: TemplateState,
  language: Language,
  onOpen: (decisionId: string) => void,
): AgendaItemCardProps[] => {
  const templateId = state.selected?.template.id;
  if (!templateId) return [];
  return selectedMatters(state, language).map((item) =>
    templateMatterCard(item, {
      onOpen,
      attachmentUrl: (attachmentId) =>
        templateAttachmentUrl(state.source, templateId, attachmentId),
    }),
  );
};

export const selectedTemplateMatter = (
  state: TemplateState,
  selected: ProjectTemplateState,
  language: Language,
): TemplateMatterItem | undefined =>
  selectedMatters(state, language).find((item) => item.decisionId === selected.selectedMatterId);

export const openMatter = (decisionId: string): DCEvent[] => [
  updateSelectedTemplateState(() => ({ selectedMatterId: decisionId })),
];

export const closeMatter = (): DCEvent[] => [
  updateSelectedTemplateState(() => ({ selectedMatterId: undefined })),
];

export const selectedPersonalisedDocumentId = (state: TemplateState): string | undefined => {
  const selected = state.selected;
  switch (selected?.type) {
    case TemplateType.PROJECT: {
      const template = templateInScope(selected);
      const tabId = selectedPanelTabId(state, template);
      return allDocuments(template.data).find(
        (document) => document.id === tabId && document.isPersonalised,
      )?.id;
    }
    case TemplateType.MAP:
    case undefined:
      return undefined;
  }
};

const selectedDocumentRecipients = (
  state: TemplateState,
  selected: ProjectTemplateState,
): ProjectTemplateRecipient[] | undefined => {
  if (!selectedPersonalisedDocumentId(state)) return undefined;
  return isOk(selected.content) ? selected.content.data.recipients : [];
};

export const updateSelectedRecipient = (): DCEvent =>
  updateSelectedTemplateState((selected, state) => {
    const recipients = selectedDocumentRecipients(state, selected);
    const chosen = recipients && chosenRecipients(recipients, selected);
    return { selectedRecipientId: chosen?.shown[chosen.index]?.id };
  });

export const templateRecipientPreview = (
  state: TemplateState,
  selected: ProjectTemplateState,
  dispatch: (events: DCEvent[]) => void,
): RecipientPreview | undefined => {
  const recipients = selectedDocumentRecipients(state, selected);
  return (
    recipients &&
    recipientPreview(recipients, selected, {
      selectPreviewAs: (category) => dispatch(selectPreviewAs(category)),
      selectRecipient: (recipient) => dispatch(selectRecipient(recipient)),
    })
  );
};

export const selectPreviewAs = (category: ProjectTemplateRecipientCategory): DCEvent[] => [
  updateSelectedTemplateState(() => ({ previewAs: category })),
  updateSelectedRecipient(),
];

export const selectRecipient = (recipient: ProjectTemplateRecipient): DCEvent[] => [
  updateSelectedTemplateState(() => ({ selectedRecipientId: recipient.id })),
];

export const selectedDocumentPdfUrl = (
  state: TemplateState,
  selected: ProjectTemplateState,
  language: Language,
): string | undefined => {
  const tab = selectedPanelTab(state, language);
  if (tab?.type !== "document") return undefined;
  const waitsForRecipients =
    selectedPersonalisedDocumentId(state) === tab.id &&
    !isOk(selected.content) &&
    !isFailure(selected.content);
  if (waitsForRecipients) return undefined;
  return templateDocumentPdfUrl(
    state.source,
    selected.template.id,
    tab.id,
    selected.selectedRecipientId,
    selected.scopeRemovals,
  );
};

export const openTemplatePanel = (templateId: string): DCEvent[] => [
  updateTemplateState((state) => {
    const template = projectTemplates(state).find((it) => it.id === templateId);
    if (!template) return {};
    return {
      panelTabId: undefined,
      selected: {
        type: TemplateType.PROJECT,
        template,
        content: projectContent(state.contents[templateId]),
        scopeRemovals:
          state.selected?.type === TemplateType.PROJECT && state.selected.template.id === templateId
            ? state.selected.scopeRemovals
            : noRemovals,
      },
    };
  }),
  ...fetchTemplateContent(),
];

export const savedMapSettingLabels = (template: MapTemplateDTO): TMessageKey[] =>
  template.data.settings
    .filter((setting) => !template.data.excluded.includes(setting))
    .map((setting) => mapTemplateSettingLabels[setting]);

export const openMapTemplatePanel = (
  templateId: string,
  previewCompany?: TemplatePreviewCompany,
): DCEvent[] => [
  updateTemplateState((state) => {
    const template = mapTemplates(state).find((it) => it.id === templateId);
    return template
      ? { panelTabId: undefined, selected: { type: TemplateType.MAP, template, previewCompany } }
      : {};
  }),
  ...(previewCompany ? fetchMapTemplateContent(templateId, previewCompany) : []),
];

const fetchMapTemplateContent = (
  templateId: string,
  company: TemplatePreviewCompany,
): DCEvent[] => [
  updateSelectedMapState(() => ({ content: pending() })),
  templateApiClient.getContent(templateId, company.companyId, {
    onSuccess: (res) => [
      updateSelectedMapState((selected) =>
        showsSelectedPreview(selected, templateId, company)
          ? { content: mapContent(ok(res.data)) }
          : {},
      ),
    ],
    onFailure: () => [
      updateSelectedMapState((selected) =>
        showsSelectedPreview(selected, templateId, company) ? { content: failure() } : {},
      ),
    ],
  }),
];

const showsSelectedPreview = (
  selected: MapTemplateState,
  templateId: string,
  company: TemplatePreviewCompany,
): boolean =>
  selected.template.id === templateId && selected.previewCompany?.companyId === company.companyId;

const updateSelectedMapState = (
  changes: (selected: MapTemplateState) => Partial<MapTemplateState>,
): DCEvent =>
  updateTemplateState((state) => {
    const selected = state.selected;
    switch (selected?.type) {
      case TemplateType.MAP:
        return { selected: { ...selected, ...changes(selected) } };
      case TemplateType.PROJECT:
      case undefined:
        return {};
    }
  });

const mapContent = (result: Result<TemplateContent>): Result<MapTemplateContent> | undefined => {
  if (!isOk(result)) return result;
  switch (result.data.type) {
    case TemplateType.MAP:
      return ok(result.data);
    case TemplateType.PROJECT:
      return failure();
  }
};

export const defaultPreviewCompany = (
  template: TemplateDTO,
  companies: TemplatePreviewCompany[],
): TemplatePreviewCompany | undefined =>
  companies.find((company) => company.orgNo === template.company?.organisationNumber) ??
  companies[0];

const selectPreviewCompany = (templateId: string, company: TemplatePreviewCompany): DCEvent[] => [
  updateSelectedMapState(() => ({ previewCompany: company })),
  ...fetchMapTemplateContent(templateId, company),
];

export type TemplatePreviewCompanySelectProps = {
  menu: DropdownMenuProps;
  selectPrev?: () => void;
  selectNext?: () => void;
};

export const prepTemplatePreviewCompanySelect = (
  selected: MapTemplateState,
  companies: TemplatePreviewCompany[],
  dispatch: (events: DCEvent[]) => void,
): TemplatePreviewCompanySelectProps => {
  const index = companies.findIndex((company) => company.orgNo === selected.previewCompany?.orgNo);
  const select = (company: TemplatePreviewCompany) =>
    selectPreviewCompany(selected.template.id, company);
  const previous = companies[index - 1];
  const next = companies[index + 1];
  const search = selected.companySearch ?? "";
  return {
    menu: {
      button: { type: "select", label: companies[index]?.name },
      filter: search,
      filterPlaceholder: { key: "search" },
      onFilterChange: (text) => dispatch([updateSelectedMapState(() => ({ companySearch: text }))]),
      items: orderlessFilter(companies, (company) => [company.name, company.orgNo], search).map(
        (company) => ({
          type: "radio-item",
          label: company.name,
          isSelected: company.orgNo === selected.previewCompany?.orgNo,
          onSelect: [
            updateSelectedMapState(() => ({ companySearch: undefined })),
            ...select(company),
          ],
        }),
      ),
    },
    selectPrev: when(previous, (company) => () => dispatch(select(company))),
    selectNext: when(next, (company) => () => dispatch(select(company))),
  };
};

export const updateSelectedTemplate = (): DCEvent =>
  updateTemplateState((state) => {
    const selected = state.selected;
    switch (selected?.type) {
      case TemplateType.PROJECT: {
        const template = projectTemplates(state).find((it) => it.id === selected.template.id);
        return { selected: template && { ...selected, template } };
      }
      case TemplateType.MAP: {
        const template = mapTemplates(state).find((it) => it.id === selected.template.id);
        return { selected: template && { ...selected, template } };
      }
      case undefined:
        return {};
    }
  });

export const closeTemplatePanel = (): DCEvent[] => [
  updateTemplateState(() => ({ selected: undefined })),
];

export const templateUpdated = (templateId: string, templates: TemplateDTO[]): DCEvent[] => [
  updateTemplateState((state) => ({
    templatesResult: ok(templates),
    contents: omit(state.contents, templateId),
  })),
  updateSelectedTemplate(),
  withTemplateState((state) => {
    switch (state.selected?.type) {
      case TemplateType.PROJECT:
        return [
          updateSelectedTemplateState(() => ({ content: undefined })),
          updateSelectedRecipient(),
          ...fetchTemplateContent(),
        ];
      case TemplateType.MAP: {
        const { template, previewCompany } = state.selected;
        return template.id === templateId && previewCompany
          ? fetchMapTemplateContent(templateId, previewCompany)
          : [];
      }
      case undefined:
        return [];
    }
  }),
];

export const accountAdminTemplateItems = (
  template: TemplateDTO,
  accountAdminActions: AccountAdminTemplateActions,
): DropdownMenuItems => [
  {
    type: "item",
    label: { key: "enterprise-account.templates.settings" },
    icon: "gear",
    onSelect: accountAdminActions.openSettings(template),
  },
  {
    type: "item",
    label: { key: "enterprise-account.templates.delete" },
    icon: "trash",
    color: "error",
    onSelect: accountAdminActions.openDelete(template),
  },
];

export const templateOptionsMenu = (
  template: TemplateDTO,
  accountAdminActions: AccountAdminTemplateActions,
): DropdownMenuProps => ({
  button: { type: "link", label: { key: "options" } },
  items: accountAdminTemplateItems(template, accountAdminActions),
});

export const scopeItems = (template: ProjectTemplateDTO): ProjectTemplateAttachmentDTO[] =>
  [...template.data.matters, ...template.data.documents].flatMap((item) => item.attachments);

export const createProjectFromTemplateRequest = (
  orgNo: string,
  template: ProjectTemplateDTO,
  scope: TemplateScope,
): CreateProjectRequest => ({
  orgNo,
  projectName: scope.projectName.validatedValue,
  template: {
    templateId: template.id,
    decisionIds: template.data.matters.map((matter) => matter.id),
    documentIds: template.data.documents.map((document) => document.id),
    attachmentIds: scopeItems(template).map((file) => file.id),
  },
});

export const templateDialog = (state: TemplateState): ProjectTemplateDialogState | undefined =>
  state.source.type === "company" ? state.source.dialog : undefined;

const withTemplateDialog = (
  state: TemplateState,
  changes: Partial<ProjectTemplateDialogState>,
): Partial<TemplateState> =>
  state.source.type === "company" && state.source.dialog
    ? { source: { ...state.source, dialog: { ...state.source.dialog, ...changes } } }
    : {};

const setCreating = (isCreating: boolean): DCEvent =>
  updateTemplateState((state) => withTemplateDialog(state, { isCreating }));

export const createProjectFromTemplate = (
  orgNo: string,
  template: ProjectTemplateDTO,
): DCEvent[] => [
  withTemplateState((state) => {
    const scope = templateDialog(state)?.scope;
    if (!scope?.projectName.validate()) return [];
    return [
      setCreating(true),
      projectApi.createProject(createProjectFromTemplateRequest(orgNo, template, scope), {
        onSuccess: (res) => [
          ...closeTemplateDialog(),
          navigate(adminProject({ orgNo, projectId: res.data.projectId }), {
            state: { type: "project-navigation-state", from: "create-new" },
          }),
          showSuccess({ key: "dbot.template-project.success" }),
        ],
        onFailure: () => [setCreating(false), showGeneralError()],
      }),
    ];
  }),
];

const openScope = (template: TemplateDTO): DCEvent[] => [
  withTemplateState((state) =>
    templateDialog(state)?.scope?.templateId === template.id
      ? []
      : [
          updateTemplateState((current) =>
            withTemplateDialog(current, {
              scope: {
                templateId: template.id,
                projectName: new StringFieldModel({ value: template.name, required: true }),
              },
            }),
          ),
          updateSelectedTemplateState(() => ({ scopeRemovals: noRemovals })),
        ],
  ),
];

export const companyAdminTemplateActions = (state: TemplateState): CompanyAdminTemplateActions => ({
  scope: templateDialog(state)?.scope,
  isCreating: templateDialog(state)?.isCreating ?? false,
  openScope,
});

export const hasScopeContent = (template: ProjectTemplateDTO): boolean =>
  template.data.matters.length > 0 || template.data.documents.length > 0;

export const canCreateFromScope = (template: ProjectTemplateDTO, scope: TemplateScope): boolean =>
  hasScopeContent(template) && !scope.projectName.isEmpty;

export type ScopeSource = { id: string; label: TMessage | string; icon: IconName; bg?: BgColor };

export const scopeSources = (template: ProjectTemplateDTO, language: Language): ScopeSource[] => [
  ...templateMatters(template.data, language).map<ScopeSource>((matter) => ({
    id: matter.decisionId,
    label: matter.title,
    icon: getDecisionIcon(template.data.matters.find((it) => it.id === matter.decisionId)?.type),
    bg: "alt1",
  })),
  ...scopeItems(template).map<ScopeSource>((file) => ({
    id: file.id,
    label: file.filename,
    icon: "paperclip",
  })),
];

export const isMatterSource = (template: ProjectTemplateDTO, source: ScopeSource): boolean =>
  template.data.matters.some((matter) => matter.id === source.id);

const removeFileFromScope = (fileId: string): DCEvent[] => [
  updateSelectedTemplateState((selected) => ({
    scopeRemovals: {
      ...selected.scopeRemovals,
      attachmentIds: [...selected.scopeRemovals.attachmentIds, fileId],
    },
  })),
];

const removeMatterFromScope = (decisionId: string): DCEvent[] => [
  updateSelectedTemplateState((selected) => ({
    scopeRemovals: {
      ...selected.scopeRemovals,
      decisionIds: [...selected.scopeRemovals.decisionIds, decisionId],
    },
    selectedMatterId:
      selected.selectedMatterId === decisionId ? undefined : selected.selectedMatterId,
  })),
];

export const removeScopeSource = (template: ProjectTemplateDTO, source: ScopeSource): DCEvent[] =>
  isMatterSource(template, source)
    ? removeMatterFromScope(source.id)
    : removeFileFromScope(source.id);

export const showMatterInPanel = (decisionId: string): DCEvent[] => [
  updateSelectedTemplateState(() => ({ selectedMatterId: decisionId })),
  updateTemplateState(() => ({ panelTabId: "matters" })),
];

export const isMatterShownInPanel = (
  state: TemplateState,
  selected: ProjectTemplateState,
  decisionId: string,
): boolean => state.panelTabId === "matters" && selected.selectedMatterId === decisionId;

export type TemplatePanelHeaderProps = {
  title: string;
  options?: DropdownMenuProps;
  onClose: DCEvent[];
};

export const prepTemplatePanelHeader = (
  selected: SelectedTemplateState,
  context: TemplateViewContext,
): TemplatePanelHeaderProps => ({
  title: shownTemplate(selected).name,
  options: templatePanelOptions(selected, context),
  onClose: closeTemplatePanel(),
});

const templatePanelOptions = (
  selected: SelectedTemplateState,
  context: TemplateViewContext,
): DropdownMenuProps | undefined =>
  context.type === "account"
    ? templateOptionsMenu(shownTemplate(selected), context.accountAdminActions)
    : undefined;

export type TemplatePanelBodyProps =
  | { type: "details"; details: TemplateDetailsTabProps }
  | { type: "document"; document: TemplateDocumentTabProps }
  | { type: "matters"; matters: TemplateMattersTabProps }
  | { type: "map"; map: TemplateMapTabProps };

export const prepTemplatePanelBody = ({
  state,
  selected,
  context,
  dispatch,
  language,
}: {
  state: TemplateState;
  selected: SelectedTemplateState;
  context: TemplateViewContext;
  dispatch: (events: DCEvent[]) => void;
  language: Language;
}): TemplatePanelBodyProps => {
  switch (selected.type) {
    case TemplateType.PROJECT:
      return prepProjectTemplatePanelBody(state, selected, dispatch, language);
    case TemplateType.MAP:
      return prepMapTemplatePanelBody(state, selected, context, dispatch, language);
  }
};

const prepProjectTemplatePanelBody = (
  state: TemplateState,
  selected: ProjectTemplateState,
  dispatch: (events: DCEvent[]) => void,
  language: Language,
): TemplatePanelBodyProps => {
  const template = templateInScope(selected);
  const tab = selectedTab(projectTemplatePanelTabs(template.data, language), state, template);
  switch (tab.type) {
    case "details":
      return { type: "details", details: prepTemplateDetailsTab(selected, language) };
    case "document":
      return {
        type: "document",
        document: prepTemplateDocumentTab(state, selected, tab, dispatch, language),
      };
    case "matters":
      return {
        type: "matters",
        matters: prepTemplateMattersTab(state, selected, dispatch, language),
      };
  }
};

const prepMapTemplatePanelBody = (
  state: TemplateState,
  selected: MapTemplateState,
  context: TemplateViewContext,
  dispatch: (events: DCEvent[]) => void,
  language: Language,
): TemplatePanelBodyProps => {
  const tab = selectedTab(mapTemplatePanelTabs, state, selected.template);
  switch (tab.type) {
    case "details":
      return { type: "details", details: prepTemplateDetailsTab(selected, language) };
    case "map":
      return { type: "map", map: prepTemplateMapTab(selected, context, dispatch) };
  }
};

const selectedTab = <T extends TemplatePanelTab>(
  tabs: T[],
  state: TemplateState,
  template: TemplateDTO,
): T | DetailsPanelTab =>
  tabs.find((tab) => tab.id === selectedPanelTabId(state, template)) ?? detailsTab;

export type TemplateDetailRow = { label: TMessageKey; value?: TMessage | TMessageParam };

export type TemplateDetailsTabProps = {
  details: TemplateDetailRow[];
  contents: TemplateDetailRow[];
};

const prepTemplateDetailsTab = (
  selected: SelectedTemplateState,
  language: Language,
): TemplateDetailsTabProps => {
  const template = shownTemplate(selected);
  return {
    details: [
      { label: "enterprise-account.templates.title", value: { translated: template.name } },
      {
        label: "general.description",
        value: when(template.description, (description) => ({ translated: description })),
      },
      {
        label: "enterprise-account.templates.created-by",
        value: when(template.createdByName, (name) => ({ translated: name })),
      },
      {
        label: "enterprise-account.templates.created-date",
        value: { type: "date", value: template.createdAt },
      },
      { label: "type", value: templateSubtitle(template) },
      {
        label: "enterprise-account.templates.visibility",
        value: { key: "enterprise-account.templates.visibility.all-admins" },
      },
      {
        label: "enterprise-account.templates.created.in",
        value: when(template.company?.companyName, (name) => ({ translated: name })),
      },
    ],
    contents: templateContentRows(selected, language),
  };
};

const templateContentRows = (
  selected: SelectedTemplateState,
  language: Language,
): TemplateDetailRow[] => {
  switch (selected.type) {
    case TemplateType.PROJECT: {
      const template = templateInScope(selected);
      return [
        countRow("enterprise-account.templates.filter.matters", template.data.matters.length),
        countRow(
          "enterprise-account.templates.filter.documents",
          templateDocuments(template.data, language).length,
        ),
        countRow("enterprise-account.templates.files", templateAttachmentCount(template)),
      ];
    }
    case TemplateType.MAP:
      return [
        {
          label: "settings",
          value: {
            type: "list",
            value: savedMapSettingLabels(selected.template).map((key) => ({ key })),
          },
        },
      ];
  }
};

const countRow = (label: TMessageKey, count: number): TemplateDetailRow => ({
  label,
  value: count === 0 ? undefined : { type: "number", value: count },
});

export type TemplateDocumentTabProps = {
  recipient?: RecipientPreview;
  pdf?: { filename: string; url: string };
  files: TemplateDocumentFile[];
};

const prepTemplateDocumentTab = (
  state: TemplateState,
  selected: ProjectTemplateState,
  tab: ProjectTemplatePanelTab,
  dispatch: (events: DCEvent[]) => void,
  language: Language,
): TemplateDocumentTabProps => ({
  recipient: templateRecipientPreview(state, selected, dispatch),
  pdf: when(selectedDocumentPdfUrl(state, selected, language), (url) => ({
    filename: `${tab.id}.pdf`,
    url,
  })),
  files: templateDocumentFiles(state.source, templateInScope(selected), tab.id),
});

export type TemplateMattersTabProps =
  | { view: "loading" }
  | { view: "unavailable" }
  | { view: "list"; cards: AgendaItemCardProps[] }
  | { view: "open"; card: AgendaItemCardProps; rows: string[][]; text?: string };

const prepTemplateMattersTab = (
  state: TemplateState,
  selected: ProjectTemplateState,
  dispatch: (events: DCEvent[]) => void,
  language: Language,
): TemplateMattersTabProps => {
  const content = selected.content;
  if (isFailure(content)) return { view: "unavailable" };
  if (!isOk(content)) return { view: "loading" };
  const cards = templateMatterCards(state, language, (decisionId) =>
    dispatch(openMatter(decisionId)),
  );
  const matter = selectedTemplateMatter(state, selected, language);
  const card = cards.find((it) => it.id === selected.selectedMatterId);
  return matter && card
    ? {
        view: "open",
        card: { ...card, onClose: closeMatter() },
        rows: content.data.matters.find((it) => it.decisionId === matter.decisionId)?.rows ?? [],
        text: matter.text,
      }
    : { view: "list", cards };
};

export type TemplateMapTabProps = {
  companySelect: TemplatePreviewCompanySelectProps;
  graph:
    | { view: "loading" }
    | { view: "unavailable" }
    | { view: "graph"; props: DataGraphProps<OwnershipNode, OwnershipEdge> };
};

const prepTemplateMapTab = (
  selected: MapTemplateState,
  context: TemplateViewContext,
  dispatch: (events: DCEvent[]) => void,
): TemplateMapTabProps => ({
  companySelect: prepTemplatePreviewCompanySelect(
    selected,
    context.type === "account" ? context.companies : [],
    dispatch,
  ),
  graph: isFailure(selected.content)
    ? { view: "unavailable" }
    : isOk(selected.content)
      ? {
          view: "graph",
          props: mapTemplateGraphProps(selected.content.data),
        }
      : { view: "loading" },
});

export const mapTemplateGraphProps = ({
  settings,
  graph,
}: MapTemplateContent): DataGraphProps<OwnershipNode, OwnershipEdge> => ({
  ...ownershipGraphProps({
    graph,
    graphType: settings.graphType,
    filterGroup: settings.values[MapTemplateSetting.FILTERS],
    pageFormat: settings.values[MapTemplateSetting.FORMAT],
    nodeSettings: settings.values[MapTemplateSetting.NODE_SETTINGS] ?? {},
  }),
  locked: true,
});

export type TemplateScopeFooterProps = {
  state: TemplateState;
  actions: CompanyAdminTemplateActions;
  template: ProjectTemplateDTO;
  selected: ProjectTemplateState;
};

export type TemplatePanelFooterProps = {
  type: TemplateType.PROJECT;
  scope?: TemplateScopeFooterProps;
};

export const prepTemplatePanelFooter = (
  state: TemplateState,
  selected: SelectedTemplateState,
  context: TemplateViewContext,
): TemplatePanelFooterProps | undefined => {
  switch (selected.type) {
    case TemplateType.PROJECT:
      return prepProjectTemplatePanelFooter(state, selected, context);
    case TemplateType.MAP:
      return undefined;
  }
};

const prepProjectTemplatePanelFooter = (
  state: TemplateState,
  selected: ProjectTemplateState,
  context: TemplateViewContext,
): TemplatePanelFooterProps => ({
  type: TemplateType.PROJECT,
  scope:
    context.type === "company"
      ? {
          state,
          actions: context.companyAdminActions,
          template: templateInScope(selected),
          selected,
        }
      : undefined,
});

export type TemplateItem = { label: TMessage; icon: IconName };

export const templateAttachmentCount = (template: ProjectTemplateDTO): number =>
  template.data.matters.reduce((count, matter) => count + matter.attachments.length, 0);

export const closeTemplateDialog = (): DCEvent[] => [
  { type: "closeDialog" },
  updateStore({ templates: undefined }),
];

export type ProjectTemplateType =
  | { type: "matter"; subtitle: TMessage; icon: IconName }
  | { type: "agenda"; subtitle: TMessage; icon: IconName }
  | { type: "document"; subtitle: TMessage; icon: IconName }
  | { type: "documents"; subtitle: TMessage; icon: IconName }
  | { type: "board-meeting"; subtitle: TMessage; icon: IconName }
  | { type: "general-meeting"; subtitle: TMessage; icon: IconName }
  | { type: "project"; subtitle: TMessage; icon: IconName };

export const projectTemplateType = (data: ProjectTemplateDataDTO): ProjectTemplateType => {
  const decisions = data.matters.map((matter) => matter.type);
  const documents = data.documents.map((d) => d.type).filter(isDocumentType);
  switch (true) {
    case isMeeting(documents, DocumentType.notice_board_meeting, DocumentType.board_meeting):
      return {
        type: "board-meeting",
        subtitle: { key: "project-templates.subtitle.board-meeting" },
        icon: "calendar",
      };
    case isMeeting(documents, DocumentType.notice_general_meeting, DocumentType.general_meeting):
      return {
        type: "general-meeting",
        subtitle: { key: "project-templates.subtitle.general-meeting" },
        icon: "calendar",
      };
    case documents.length === 0 && decisions.length === 1:
      return { type: "matter", subtitle: getDecisionName(decisions[0]), icon: "gavel" };
    case documents.length === 0 && decisions.length > 1:
      return {
        type: "agenda",
        subtitle: { key: "project-templates.subtitle.agenda" },
        icon: "list-checks",
      };
    case decisions.length === 0 && documents.length === 1:
      return {
        type: "document",
        subtitle: getDocumentLabel(documents[0]),
        icon: isMinuteDocumentType(documents[0])
          ? "file-doc"
          : isMeetingDocumentType(documents[0])
            ? "calendar"
            : "file-text",
      };
    case decisions.length === 0 && documents.length > 1:
      return {
        type: "documents",
        subtitle: {
          key: "project-templates.subtitle.documents",
          params: { count: documents.length },
        },
        icon: "files",
      };
    default:
      return {
        type: "project",
        subtitle: { key: "project-templates.subtitle.project" },
        icon: "sparkle",
      };
  }
};

export const isMeeting = (
  documents: DocumentType[],
  notice: DocumentType,
  minutes: DocumentType,
): boolean => documents.length === 2 && documents.includes(notice) && documents.includes(minutes);

export const isMeetingDocumentType = (type: DocumentType): boolean =>
  isGeneralMeetingDocumentType(type) || isBoardMeetingDocumentType(type);

export const templateSubtitle = (template: TemplateDTO): TMessage => {
  switch (template.data.type) {
    case TemplateType.PROJECT:
      return projectTemplateSubtitle(template.data);
    case TemplateType.MAP:
      return { key: mapTemplateLabels[template.data.graphType] };
  }
};

export const projectTemplateSubtitle = (data: ProjectTemplateDataDTO): TMessage =>
  data.documents.some((document) => document.isPersonalised)
    ? {
        key: "enterprise-account.templates.subtitle.individual",
        params: { type: projectTemplateType(data).subtitle },
      }
    : projectTemplateType(data).subtitle;

export const templatesIcon = (template: TemplateDTO): IconProps => {
  switch (template.data.type) {
    case TemplateType.PROJECT:
      return { name: projectTemplateType(template.data).icon };
    case TemplateType.MAP:
      return mapTemplateIcons[template.data.graphType];
  }
};

export const mapTemplateIcons: { [type in MapTemplateGraphType]: IconProps } = {
  owner: { name: "git-fork", rotate: 180 },
  owns: { name: "git-fork" },
  dual: { name: "graph" },
};
